import React, { useRef, useState } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import { svgToIcon } from '../utils/svg';
import { type Instance as TippyInstance } from 'tippy.js/index';
import Tool, { type ToolButtonComponent } from './Tool';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolButton from '../toolbars/ToolButton';
import ToggleToolButton from '../toolbars/ToggleToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import tableHeaderOptionsIcon from '../assets/icons/stylusTableHeaderOptions.svg';
import headerRowIcon from '../assets/icons/tableHeaderRow.svg';
import headerColumnIcon from '../assets/icons/tableHeaderColumn.svg';
import { isTable } from '../objects/guards';

const Popup: React.FC<{ selection: fabric.Object }> = ApplicationView(
  ({ selection }) => {
    const controller = useController(WhiteboardController);

    if (!isTable(selection)) {
      throw new Error('Expected selection to be a table');
    }

    const [headerRowIsActive, setHeaderRowIsActive] = useState(
      selection.hasHeaderRow
    );
    const [headerColumnIsActive, setHeaderColumnIsActive] = useState(
      selection.hasHeaderColumn
    );

    const toggleOption = (
      option: 'hasHeaderRow' | 'hasHeaderColumn',
      stateSetter: typeof setHeaderColumnIsActive
    ) => {
      const newValue = !(option === 'hasHeaderRow'
        ? headerRowIsActive
        : headerColumnIsActive);
      selection.set(option, newValue);
      controller.canvas.requestRenderAll();
      Tool.toolActionComplete({ controller });
      stateSetter(newValue);
    };

    return (
      <div>
        <ToolbarMenu columns={5}>
          <ToggleToolButton
            name={'Header row'}
            icon={headerRowIcon}
            active={headerRowIsActive}
            enabled={true}
            onClick={() => toggleOption('hasHeaderRow', setHeaderRowIsActive)}
            testId='Table-toggle-header-row'
          />
          <ToggleToolButton
            name={'Header column'}
            icon={headerColumnIcon}
            active={headerColumnIsActive}
            enabled={true}
            onClick={() =>
              toggleOption('hasHeaderColumn', setHeaderColumnIsActive)
            }
            testId='Table-toggle-header-column'
          />
        </ToolbarMenu>
      </div>
    );
  }
);

const TableHeaderOptionsButton: ToolButtonComponent = ApplicationView(
  ({ selection, tool }) => {
    const tippyRef = useRef<TippyInstance>();

    return (
      <ToolButton
        popup={<Popup selection={selection} />}
        onClick={() =>
          tippyRef.current?.state.isShown && tippyRef.current?.hide()
        }
        tool={tool}
        tippyRef={tippyRef}
      >
        {svgToIcon(tableHeaderOptionsIcon)}
      </ToolButton>
    );
  }
);

class ToolTableHeaderOptions extends Tool {
  static toolName = 'Table header';

  static buttonComponent() {
    return TableHeaderOptionsButton;
  }
}

export { ToolTableHeaderOptions };
